import type { GetStaticPaths, NextPage } from 'next'
import React, { useEffect } from 'react'

import { ApiHandler } from '@hozana/api/ApiHandler'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { createGetServerSideData, createGetServerSideProps } from '@hozana/api/server/serverSide'
import { useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { getFirstQueryValue, getFullUrl, translateUrl } from '@hozana/router/functions'
import { useScreen } from '@hozana/screen/useScreen'
import { IMAGE_SIZES, getImageUrl } from '@hozana/styling/image'

import { Block } from 'elements/layout/Block'
import { GradientDiv } from 'elements/ui/GradientDiv'

import { Head } from 'general/structure/Head'
import { Page } from 'general/structure/Page'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { CONNECT_FORM_WIDTH } from 'modules/auth/components/ConnectForm/constants'
import { JoinConnectForm } from 'modules/community/components/JoinConnectForm'
import { communityNameTitleGetter } from 'modules/community/functions'
import { getCommunityQuery } from 'modules/community/queries'
import { selectCommunity } from 'modules/community/selectors'
import type { TCommunity } from 'modules/community/types'
import { useUser } from 'modules/user/hooks/useUser'

const communityImageSize = IMAGE_SIZES.community.big

/** Get canonical link for the head html tag */
const getHeadLinks = (community: TCommunity) => ({
  canonical: getFullUrl(
    {
      pathname: PAGE.COMMUNITY_VIEW_LANDING,
      query: {
        communityId: community.id,
        communitySlug: community.slug,
      },
    },
    community.lang,
  ),
})

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' })

export const getServerSideProps = createGetServerSideProps(
  PAGE.COMMUNITY_VIEW_LANDING,
  communityNameTitleGetter,
  async (ctx) => {
    const communityId = Number(ctx.params.communityId)
    const data = await createGetServerSideData(({ params }) => ({
      community: {
        query: getCommunityQuery({ communityId: Number(params.communityId) }),
        displayErrors: true,
      },
    }))(ctx)

    if ('props' in data) {
      const community = selectCommunity(ctx.store.getState(), communityId)
      if (community.hasJoined) {
        return {
          redirect: {
            permanent: false,
            destination: translateUrl(
              {
                pathname: PAGE.COMMUNITY_VIEW,
                query: {
                  communityId: community.id,
                  communitySlug: community.slug,
                },
              },
              community.lang,
              {
                format: 'string',
              },
            ),
          },
        }
      }
    }
    return data
  },
  { namespaces: [NAMESPACE.COMMUNITY, NAMESPACE.PRAY] },
)

/**
 * ### CommunityLandingPage ###
 */
export const CommunityLandingPage: NextPage = () => {
  const me = useUser()
  const screen = useScreen()
  const { query, replace } = useRouter() // #router.replace
  const { communityId: stringCommunityId, communitySlug, ...searchParams } = getFirstQueryValue(query)
  const communityId = parseInt(stringCommunityId, 10)

  const [, communityQuery] = useApiData(
    () => ({
      query: getCommunityQuery({ communityId }),
      disabled: !communityId || !me.isLogged,
    }),
    [communityId, me.isLogged],
  )
  const community = useSelector((state) => selectCommunity(state, communityId))

  const communityImage = community && getImageUrl(community.picture, communityImageSize)
  const [communityImageWidth, communityImageHeight] = (community && communityImageSize.split('x')) || []

  // Ensure correct url
  useEffect(() => {
    if (community) {
      if (communitySlug !== community.slug) {
        replace({
          pathname: PAGE.COMMUNITY_VIEW_LANDING,
          query: {
            communityId: community.id,
            communitySlug: community.slug,
            ...searchParams,
          },
        })
      }
    }
  }, [community, replace, communitySlug, searchParams])

  // If no community slug is defined, wait for page reload before rendering
  if (!communitySlug) {
    return null
  }

  return (
    <Page>
      <ApiHandler
        queries={[communityQuery]}
        display="fullPage"
        onRetry={() => {
          communityQuery.refetch()
        }}
        noLoadingDisplay
      />
      {community && (
        <>
          <Head
            description={community.introduction}
            isIndexed={false}
            title={community.name}
            image={community.picture && communityImage}
            imageWidth={community.picture && communityImageWidth}
            imageHeight={community.picture && communityImageHeight}
            links={community ? getHeadLinks(community) : undefined}
          />

          {/* CONTENT */}
          <GradientDiv
            minH="100vh"
            firstColor="lightPurple"
            middleColor="lightBlue"
            lastColor="whiteOrange"
            p={{ xs: '0', sm: '10vh 10px 100px' }}
          >
            <Block
              maxW={{ xs: '100%', sm: CONNECT_FORM_WIDTH }}
              noOverflow={!screen.xs}
              p={{ xs: '0', sm: '0 0 1em 0' }}
              /**
               * h="100%" + minH="100vh" because otherwise it does not take the whole visible height
               * on mobile when the virtual keyboard is open
               */
              h={screen.xs ? '100%' : undefined}
              minH={screen.xs ? '100vh' : undefined}
              bg="white"
              m="auto"
            >
              <JoinConnectForm community={community} />
            </Block>
          </GradientDiv>
        </>
      )}
    </Page>
  )
}
